/**
 * Notifications — standard card, design 8b: type filter collapsed into one
 * dropdown, refresh / mark-all-read / clear as icon buttons in the header,
 * the date on the title line, unread as a left purple edge, and the footer
 * carrying the overflow link, which opens the full list in a modal.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';
import Modal from '@shared/ui/Modal';
import Button from '../../Button';
import { ConfirmIcon, DeleteIcon, RefreshIcon, SpinnerIcon } from '../../icons';

interface NotificationItem {
  id: string;
  notification_type: string;
  title: string;
  message?: string;
  is_read: boolean;
  created_at: string;
}

/*
 * Short labels for the type filter. Keys are the notification_type strings
 * the backend actually writes — the NOTIFICATION_TYPES registry in
 * backend/src/services/notificationService.js. Unknown future types fall
 * back to a humanized version of the raw string.
 */
const TYPE_LABELS: Record<string, string> = {
  bill_added_to_workspace: 'Bill updates',
  note_created: 'Notes',
  note_shared: 'Notes',
  workspace_invitation: 'Invites',
  calendar_event_created: 'Calendar',
  system_announcement: 'Announcements',
  outreach_recipient_responded: 'Outreach',
  outreach_response_requested: 'Outreach',
  outreach_update: 'Outreach',
};

function typeLabel(type: string): string {
  return (
    TYPE_LABELS[type] ??
    type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  );
}

const COLLAPSED_COUNT = 5;

function NotificationRow({
  notification,
  onRead,
}: {
  notification: NotificationItem;
  onRead: (id: string) => void;
}) {
  const n = notification;
  return (
    <button
      type="button"
      onClick={() => {
        if (!n.is_read) onRead(n.id);
      }}
      className={`w-full text-left px-4 py-2.5 border-b border-border-light dark:border-border-dark-hairline last:border-b-0 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary ${
        n.is_read
          ? ''
          : 'border-l-[3px] border-l-primary-500 pl-[13px] bg-neutral-50 dark:bg-background-dark-tertiary'
      }`}
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold text-primary-800 dark:text-text-dark-primary leading-snug">
          {n.title}
        </span>
        <span className="text-[11px] text-slate-400 dark:text-text-dark-tertiary tabular-nums flex-none">
          {new Date(n.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </span>
      </span>
      {n.message && (
        <span className="block text-xs text-slate-500 dark:text-text-dark-secondary mt-0.5 line-clamp-2">
          {n.message}
        </span>
      )}
    </button>
  );
}

const NotificationsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: items, isLoading, isFetching, refetch } = useQuery(
    ['proHome', 'notifications'],
    async (): Promise<NotificationItem[]> => {
      const response = await api.get('/notifications?limit=50&offset=0');
      return response.data?.data?.notifications ?? [];
    },
    { staleTime: 60 * 1000, refetchInterval: 2 * 60 * 1000 },
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['proHome', 'notifications'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-bell-list'] });
  };

  const markAllRead = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: invalidate,
  });
  const clearAll = useMutation({
    mutationFn: () => api.delete('/notifications'),
    onSuccess: invalidate,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: invalidate,
  });

  // Filter by display label, not raw type — several raw types share a label
  // (note_created/note_shared → "Notes", the outreach family → "Outreach").
  const presentLabels = useMemo(
    () =>
      Array.from(new Set((items ?? []).map((n) => typeLabel(n.notification_type)))).sort(),
    [items],
  );

  const filtered = useMemo(
    () =>
      typeFilter === 'all'
        ? (items ?? [])
        : (items ?? []).filter((n) => typeLabel(n.notification_type) === typeFilter),
    [items, typeFilter],
  );

  const visible = filtered.slice(0, COLLAPSED_COUNT);
  const newCount = filtered.filter((n) => !n.is_read).length;

  const filterSelect = (
    <select
      value={typeFilter}
      onChange={(e) => setTypeFilter(e.target.value)}
      aria-label="Filter by type"
      className="input py-1 px-2 text-xs w-32 flex-none"
    >
      <option value="all">All types</option>
      {presentLabels.map((label) => (
        <option key={label} value={label}>
          {label}
        </option>
      ))}
    </select>
  );

  return (
    <section
      className="card p-0 flex flex-col overflow-hidden min-h-0"
      aria-label="Notifications"
      data-testid="dashboard-notifications"
    >
      <div className="flex-none px-4 pt-3.5 pb-3 border-b border-border-light dark:border-border-dark-hairline flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
            Notifications
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
              }}
              disabled={isFetching}
              title="Refresh"
              aria-label="Refresh notifications"
              className="rounded-md p-1 text-slate-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary disabled:opacity-40"
            >
              <RefreshIcon
                className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isLoading || newCount === 0}
              title="Mark all read"
              aria-label="Mark all read"
              className="rounded-md p-1 text-slate-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ConfirmIcon className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => clearAll.mutate()}
              disabled={clearAll.isLoading || (items ?? []).length === 0}
              title="Clear all"
              aria-label="Clear all notifications"
              className="rounded-md p-1 text-slate-400 hover:text-error-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <DeleteIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {filterSelect}
          <span className="text-xs text-slate-500 dark:text-text-dark-secondary">
            {filtered.length} item{filtered.length === 1 ? '' : 's'} ·{' '}
            {newCount > 0 ? `${newCount} new` : 'none new'}
          </span>
        </div>
      </div>

      <div className="pro-home-scroll flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <SpinnerIcon className="w-5 h-5 animate-spin text-primary-400" aria-hidden="true" />
          </div>
        ) : visible.length === 0 ? (
          <p className="px-4 py-8 text-sm text-slate-500 dark:text-text-dark-secondary text-center">
            You're all caught up.
          </p>
        ) : (
          visible.map((n) => (
            <NotificationRow key={n.id} notification={n} onRead={(id) => markRead.mutate(id)} />
          ))
        )}
      </div>

      {filtered.length > COLLAPSED_COUNT && (
        <div className="flex-none border-t border-border-light dark:border-border-dark-hairline bg-neutral-50 dark:bg-background-dark-tertiary px-4 py-2.5 flex items-center justify-between">
          <span className="text-[11.5px] text-slate-500 dark:text-text-dark-secondary">
            Showing {visible.length} of {filtered.length}
          </span>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:text-primary-700"
          >
            See all →
          </button>
        </div>
      )}

      {/* Full list in a modal — same rows, same filter state */}
      <Modal
        isOpen={modalOpen}
        title="Notifications"
        description={`${filtered.length} item${filtered.length === 1 ? '' : 's'} · ${
          newCount > 0 ? `${newCount} new` : 'none new'
        }`}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isLoading || newCount === 0}
            >
              Mark all read
            </Button>
            <Button variant="primary" onClick={() => setModalOpen(false)}>
              Close
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {filterSelect}
          <div className="border border-border-light dark:border-border-dark-hairline rounded-xl overflow-hidden">
            {filtered.length === 0 ? (
              <p className="px-4 py-8 text-sm text-slate-500 dark:text-text-dark-secondary text-center">
                You're all caught up.
              </p>
            ) : (
              filtered.map((n) => (
                <NotificationRow key={n.id} notification={n} onRead={(id) => markRead.mutate(id)} />
              ))
            )}
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default NotificationsCard;
